import React, { useState, useEffect } from 'react';

/**
 * SafeImage — drop-in replacement for <img> with bulletproof fallback chain.
 *
 * Behavior:
 *   1. Tries `src` first
 *   2. On error (404, expired signed URL, blocked, etc.), falls back to `fallbackSrc`
 *   3. If `fallbackSrc` also fails, shows a neutral placeholder (gradient) + alt text
 *
 * Usage:
 *   <SafeImage src={cms.banner.image_url} fallbackSrc={DEFAULT_IMAGES.hero} alt="Hero" className="..." />
 */
export const SafeImage = ({
  src,
  fallbackSrc,
  alt = '',
  className = '',
  placeholderClassName = '',
  ...imgProps
}) => {
  // Treat known-bad URLs as directly invalid (Uploadcare is deprecated)
  const isInvalidSrc = !src || src.includes('ucarecd') || src.includes('uploadcare');
  const [currentSrc, setCurrentSrc] = useState(isInvalidSrc ? fallbackSrc : src);
  const [stage, setStage] = useState(isInvalidSrc && fallbackSrc ? 'fallback' : 'primary');
  const [failed, setFailed] = useState(false);

  // Reset when props change (important for CMS content refresh)
  useEffect(() => {
    const invalid = !src || src.includes('ucarecd') || src.includes('uploadcare');
    setCurrentSrc(invalid ? fallbackSrc : src);
    setStage(invalid && fallbackSrc ? 'fallback' : 'primary');
    setFailed(false);
  }, [src, fallbackSrc]);

  const handleError = () => {
    if (stage === 'primary' && fallbackSrc && fallbackSrc !== currentSrc) {
      setCurrentSrc(fallbackSrc);
      setStage('fallback');
    } else {
      setFailed(true);
    }
  };

  if (failed || !currentSrc) {
    return (
      <div
        className={`bg-gradient-to-br from-[#1F1F1F] to-[#0B0B0B] flex items-center justify-center ${className} ${placeholderClassName}`}
        role="img"
        aria-label={alt}
        data-testid="safe-image-placeholder"
      >
        <span className="text-[#C9A24D]/40 text-xs font-['Playfair_Display']">Y GO</span>
      </div>
    );
  }

  return (
    <img
      src={currentSrc}
      alt={alt}
      className={className}
      onError={handleError}
      loading="lazy"
      {...imgProps}
    />
  );
};

export default SafeImage;
